'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  GridApiResponse,
  LobbyGridSummary,
  SeatData,
  TakeoverSDKConfig,
  V2Grid,
  V2GridPageData,
} from './types';
import { buildSeatData, mergeLiveSeats } from './seat-transform';
import { useLiveGrid } from './live-grid';

const TakeoverConfigContext = createContext<TakeoverSDKConfig | null>(null);

export function TakeoverProvider({
  config,
  children,
}: {
  config: TakeoverSDKConfig;
  children: ReactNode;
}) {
  return (
    <TakeoverConfigContext.Provider value={config}>
      {children}
    </TakeoverConfigContext.Provider>
  );
}

export function useTakeoverConfig(): TakeoverSDKConfig {
  const ctx = useContext(TakeoverConfigContext);
  if (!ctx) {
    throw new Error('@takeover/sdk: TakeoverProvider is missing in the React tree');
  }
  return ctx;
}

/**
 * Single grid snapshot from `/api/v1/grid/[ugm]/[gridId]`. Returns the raw
 * `V2GridPageData` payload (the `data` field of the API envelope).
 *
 * Most embed consumers want `useSeats()` — this hook is the escape hatch
 * for tooling that wants direct access to raw seats, reservations, etc.
 */
export function useGrid(options: {
  ugmAddress: string;
  gridId: string;
  enabled?: boolean;
  refetchIntervalMs?: number;
}): UseQueryResult<V2GridPageData, Error> {
  const { apiBaseUrl } = useTakeoverConfig();
  const base = apiBaseUrl.replace(/\/$/, '');

  return useQuery({
    queryKey: ['@takeover/sdk', 'grid', base, options.ugmAddress, options.gridId],
    queryFn: async (): Promise<V2GridPageData> => {
      const path = `${base}/api/v1/grid/${options.ugmAddress}/${options.gridId}`;
      const res = await fetch(path);
      if (!res.ok) {
        throw new Error(`@takeover/sdk: grid request failed (${res.status})`);
      }
      const json = (await res.json()) as GridApiResponse;
      return json.data;
    },
    enabled:
      options.enabled !== false && !!options.ugmAddress && !!options.gridId,
    refetchInterval: options.refetchIntervalMs,
    staleTime: 1_000,
  });
}

export function useGrids(options: {
  curation: string;
  limit: number;
}): UseQueryResult<LobbyGridSummary[], Error> {
  const { apiBaseUrl } = useTakeoverConfig();
  const base = apiBaseUrl.replace(/\/$/, '');

  return useQuery({
    queryKey: ['@takeover/sdk', 'grids', base, options.curation, options.limit],
    queryFn: async (): Promise<LobbyGridSummary[]> => {
      const q = new URLSearchParams({
        curation: options.curation,
        limit: String(options.limit),
      });
      const res = await fetch(`${base}/api/v1/grids?${q.toString()}`);
      if (!res.ok) {
        throw new Error(`@takeover/sdk: grids request failed (${res.status})`);
      }
      const json: unknown = await res.json();
      if (Array.isArray(json)) return json as LobbyGridSummary[];
      if (
        json &&
        typeof json === 'object' &&
        'data' in json &&
        Array.isArray((json as { data: unknown }).data)
      ) {
        return (json as { data: LobbyGridSummary[] }).data;
      }
      return [];
    },
  });
}

export type LiveMode = 'off' | 'poll' | 'sse';

export interface UseSeatsOptions {
  ugmAddress: string;
  gridId: string;
  /** Viewer's connected wallet — drives `seat.isOwn` flags in the render. */
  viewerAddress?: string | null;
  viewerDisplayName?: string | null;
  viewerAvatarUrl?: string | null;
  viewerLevel?: number;
  /**
   * Update strategy. Defaults to `'poll'` (every 30s) to avoid fanning out
   * SSE connections across many mounts / tabs. Set `'sse'` to opt into the
   * shared-connection live stream (one EventSource per `(ugm, gridId)` across
   * the page). Set `'off'` for a one-shot snapshot.
   */
  live?: LiveMode;
  /** Override polling interval (ms). Default 30_000. */
  pollIntervalMs?: number;
  enabled?: boolean;
}

export interface UseSeatsResult {
  seats: SeatData[];
  grid: V2Grid | undefined;
  isLoading: boolean;
  error: Error | null;
  isLive: boolean;
}

/**
 * High-level hook powering `<TakeoverGrid />`. Composes `useGrid()` +
 * `buildSeatData()` + optional `useLiveGrid()` into a single tidy return
 * shape. Builders consume this directly if they want full control over
 * rendering.
 */
export function useSeats(options: UseSeatsOptions): UseSeatsResult {
  const { apiBaseUrl } = useTakeoverConfig();
  const live: LiveMode = options.live ?? 'poll';
  const pollMs = options.pollIntervalMs ?? 30_000;

  const query = useGrid({
    ugmAddress: options.ugmAddress,
    gridId: options.gridId,
    enabled: options.enabled !== false,
    refetchIntervalMs: live === 'poll' ? pollMs : undefined,
  });

  const liveGrid = useLiveGrid({
    apiBaseUrl,
    ugmAddress: options.ugmAddress,
    gridId: options.gridId,
    enabled: live === 'sse' && options.enabled !== false,
  });

  const baseline = useMemo<SeatData[]>(() => {
    if (!query.data) return [];
    return buildSeatData(query.data, {
      viewerAddress: options.viewerAddress,
      viewerDisplayName: options.viewerDisplayName,
      viewerAvatarUrl: options.viewerAvatarUrl,
      viewerLevel: options.viewerLevel,
    });
  }, [
    query.data,
    options.viewerAddress,
    options.viewerDisplayName,
    options.viewerAvatarUrl,
    options.viewerLevel,
  ]);

  const merged = useMemo<SeatData[]>(() => {
    if (!baseline.length) return baseline;
    if (live !== 'sse' || !liveGrid.liveSeats) return baseline;
    const taxRateBps = BigInt(query.data?.grid.taxRateBps ?? 0);
    return mergeLiveSeats(baseline, liveGrid.liveSeats, taxRateBps);
  }, [baseline, live, liveGrid.liveSeats, query.data?.grid.taxRateBps]);

  return {
    seats: merged,
    grid: query.data?.grid,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isLive: live === 'sse' && liveGrid.isConnected,
  };
}
